import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { forceLogout } from "../middleware/auth/index.js";

/**
 * Migrates existing localStorage session to sessionStorage on page load.
 * This ensures existing logged-in users continue to work after deployment.
 */
const migrateSession = () => {
  if (!sessionStorage.getItem("token") && localStorage.getItem("token")) {
    sessionStorage.setItem("token", localStorage.getItem("token"));
  }
  if (!sessionStorage.getItem("user") && localStorage.getItem("user")) {
    sessionStorage.setItem("user", localStorage.getItem("user"));
  }
  if (!sessionStorage.getItem("expiresIn") && localStorage.getItem("expiresIn")) {
    sessionStorage.setItem("expiresIn", localStorage.getItem("expiresIn"));
  }
};

/**
 * Gets the user ID from storage, checking sessionStorage first then localStorage.
 */
const getUserIdFromStorage = () => {
  const userStr = sessionStorage.getItem("user") || localStorage.getItem("user");
  if (userStr && userStr !== "undefined" && userStr !== "null") {
    try {
      const user = JSON.parse(userStr);
      return user?.id || user?.userId || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Hook that guards against cross-tab session contamination.
 *
 * - Migrates existing localStorage sessions to sessionStorage on mount
 * - Listens for storage events from other tabs
 * - Forces logout if another tab logs in with a different account
 */
const useSessionGuard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialUserId = useRef(null);

  useEffect(() => {
    // Migrate existing session from localStorage to sessionStorage
    migrateSession();

    // Capture the current user ID when hook mounts
    initialUserId.current = getUserIdFromStorage();

    const handleStorageChange = (e) => {
      // Only react to changes in session-related keys
      if (e.key === "token" || e.key === "user") {
        const newUserId = getUserIdFromStorage();

        // If we had a user and the user ID has changed, force logout this tab
        if (initialUserId.current && newUserId && newUserId !== initialUserId.current) {
          forceLogout(dispatch);
          alert("Your session has changed in another tab. Please log in again.");
          navigate("/login");
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, navigate]);
};

export default useSessionGuard;